import { parseBaseModel, stripBaseFields } from "./BaseModel";
import { extractSignerAttributeBindingsFromSignatures } from "../utils/signatureUtils";

const STUDIO_FORM_FIELDS = [
  "name",
  "description",
  "status",
  "studioFormId",
  "fields",
  "sections",
  "formDetails",
  "signatures",
  "selectedCreators",
  "formUploadSections",
  "signatureAnnotations",
  "additionalFields",
  "subsequentSigning",
  "signerAttributeBindings",
  "initialerAttributeBindings",
  "initialsAnnotations",
  "initials",
  "formRules",
  "offerCompare",
  "jsonStatus",
  "pdfB64",
  "pdfName",
  "pdfSourceStudioFormDataId",
  "published",
  "extraFormSummary",
  "extraDealSummary",
  "rulesJSON",
  "rawFormJSON",
];

/**
 * Subsequent-signing PDF placements are DERIVED, not stored twice.
 *
 * `subsequentSigning` is the persisted block — role metadata plus signer rows whose `fields[]`
 * each carry their own `metadata.pdfLocation`. The studio edits those placements through a
 * `subsequentSigningAttributeBindings` array, which is what the PDF canvas draws and what a nudge
 * moves. That array is deliberately NOT persisted: a second copy of the same coordinates is what
 * lets the two drift apart.
 *
 * So the two halves have to be symmetric, and both live here at the persistence boundary so the
 * UI and the scripts share them:
 *
 *   read  — derive the bindings from the block, exactly as parseFormJSON does from a bundle.
 *           Without this the canvas got an empty array and drew nothing, so the boxes were
 *           invisible in the editor even though the data was intact.
 *   write — fold the bindings' placements back onto the block before saving. Without this a nudge
 *           moved a binding that was never persisted, and the move was lost on save.
 */
const readSubsequentSigning = (domain) =>
  domain?.subsequentSigning &&
  typeof domain.subsequentSigning === "object" &&
  !Array.isArray(domain.subsequentSigning)
    ? domain.subsequentSigning
    : {};

const deriveSubsequentBindings = (block) =>
  extractSignerAttributeBindingsFromSignatures(block).map((b) => ({
    ...b,
    id: b.id?.replace(/^signer-bind-/, "subsequent-bind-") || b.id,
  }));

/**
 * The block with each binding's pdfLocation written onto its field.
 *
 * Matched on role + signer + attributeName, positionally when a signer carries the same
 * attributeName more than once, so the Nth binding lands on the Nth field rather than all of them
 * collapsing onto the first.
 */
const applySubsequentBindings = (block, bindings) => {
  if (!block || typeof block !== "object" || Array.isArray(block)) return {};
  if (!Array.isArray(bindings) || bindings.length === 0) return block;

  const queued = new Map(); // roleKey \t signerKey \t attributeName -> [pdfLocation]
  for (const b of bindings) {
    if (!b?.roleKey || !b?.signerKey || !b?.attributeName || !b?.pdfLocation) continue;
    const k = `${b.roleKey}\t${b.signerKey}\t${b.attributeName}`;
    if (!queued.has(k)) queued.set(k, []);
    queued.get(k).push(b.pdfLocation);
  }
  if (queued.size === 0) return block;

  const taken = new Map();
  const next = JSON.parse(JSON.stringify(block));
  for (const [roleKey, roleValue] of Object.entries(next)) {
    for (const row of roleValue?.signatures || []) {
      for (const field of row?.fields || []) {
        const k = `${roleKey}\t${row?.signerKey ?? ""}\t${field?.attributeName ?? ""}`;
        const locs = queued.get(k);
        if (!locs || !locs.length) continue;
        const i = taken.get(k) ?? 0;
        const loc = locs[i] ?? locs[locs.length - 1];
        taken.set(k, i + 1);
        if (!field.metadata || typeof field.metadata !== "object") field.metadata = {};
        field.metadata.pdfLocation = JSON.parse(JSON.stringify(loc));
      }
    }
  }
  return next;
};

/**
 * Builds a StudioFormData request body from app state.
 * Strips the auto-save wrapper (timestamp, data) and returns a flat object.
 *
 * @param {Object} state - The raw state object (same shape as auto-save data)
 * @returns {Object} Clean StudioFormData for API requests
 */
export const buildStudioFormData = (state) => {
  if (!state) return {};

  const dirty = state.jsonStatus?.dirty || { form: false, rules: false, compare: false };

  return {
    name: state.name || "",
    description: state.description || "",
    status: "draft",
    studioFormId: state.studioFormId || null,
    fields: state.fields || [],
    sections: state.sections || [],
    formDetails: state.formDetails || {},
    signatures: state.signatures || {},
    selectedCreators: state.selectedCreators || [],
    formUploadSections: state.formUploadSections || [],
    signatureAnnotations: state.signatureAnnotations || [],
    additionalFields: state.additionalFields || [],
    // subsequentSigningJSON's role metadata + signer rows, carried verbatim — the same
    // treatment signatures and initials get. Role-level label/actionLabel/key ride along.
    subsequentSigning: applySubsequentBindings(
      state.subsequentSigning || {},
      state.subsequentSigningAttributeBindings,
    ),
    signerAttributeBindings: state.signerAttributeBindings || [],
    initialerAttributeBindings: state.initialerAttributeBindings || [],
    initialsAnnotations: state.initialsAnnotations || [],
    formRules: state.formRules || { formNotes: "", fields: [] },
    offerCompare: state.offerCompare || { fields: [] },
    /** Publish dirty/complete tracking only. Studio editor state is persisted as the first-class fields above (not smuggled through jsonStatus). */
    jsonStatus: { dirty },
    pdfB64: state.pdfB64 || null,
    pdfName: state.pdfName || null,
    // When the PDF is unchanged since load, the client sends no bytes and instead
    // references the source revision so the backend reuses its stored GCS object.
    pdfSourceStudioFormDataId: state.pdfSourceStudioFormDataId || null,
    extraFormSummary: state.extraFormSummary || {},
    extraDealSummary: state.extraDealSummary || {},
    rulesJSON: state.rulesJSON ?? null,
    // initialsJSON's own role metadata + rows. Initials own this; it used to be
    // inherited from the matching signaturesJSON role. See generateInitialsJSON.
    initials: state.initials ?? null,
    // Verbatim template JSON for raw / non-PDF smart forms. Only sent when set
    // (ordinary PDF forms leave it undefined so the field is omitted). Stored
    // as an object; the backend preserves its byte order.
    ...(state.rawFormJSON != null ? { rawFormJSON: state.rawFormJSON } : {}),
  };
};

/**
 * rulesJSON as stored on the revision, verbatim.
 *
 * Never fabricated. A revision with no rulesJSON yields null, and the publish builder
 * emits `{}` for it — which is what a template with no rules is supposed to publish.
 * An earlier version of this rehydrated legacy revisions by inventing
 * locked/executed blocks from the signer roles; that fabricated shape then got
 * published over real rules on every revision-based publish (--force-gcs and
 * republish-studio-to-all-buckets), including for the many templates whose rulesJSON
 * is legitimately `{}`. Studio does not author rules — it carries them.
 */
const readRulesJSON = (domain) => {
  const stored = domain.rulesJSON;
  return stored && typeof stored === "object" && !Array.isArray(stored) ? stored : null;
};

/**
 * Parses an API response into a StudioFormData object with base model fields.
 * Used for POST/PUT/GET responses that return a single StudioFormData.
 *
 * @param {Object} json - Raw API response JSON
 * @returns {Object} StudioFormData fields + base model fields (id, createdAt, etc.)
 */
export const parseStudioFormData = (json) => {
  if (!json) return {};

  const base = parseBaseModel(json);
  const domain = stripBaseFields(json);
  const signatures =
    domain.signatures && typeof domain.signatures === "object" && !Array.isArray(domain.signatures)
      ? domain.signatures
      : {};

  return {
    ...base,
    name: domain.name || "",
    description: domain.description || "",
    status: domain.status || "draft",
    studioFormId: domain.studioFormId || null,
    fields: domain.fields || [],
    sections: domain.sections || [],
    formDetails: domain.formDetails || {},
    signatures,
    selectedCreators: domain.selectedCreators || [],
    formUploadSections: domain.formUploadSections || [],
    signatureAnnotations: domain.signatureAnnotations || [],
    additionalFields: domain.additionalFields || [],
    subsequentSigning: readSubsequentSigning(domain),
    // Derived from the block above, never read off the revision — see deriveSubsequentBindings.
    subsequentSigningAttributeBindings: deriveSubsequentBindings(readSubsequentSigning(domain)),
    signerAttributeBindings: domain.signerAttributeBindings || [],
    initialerAttributeBindings: domain.initialerAttributeBindings || [],
    initialsAnnotations: domain.initialsAnnotations || [],
    formRules: domain.formRules || { formNotes: "", fields: [] },
    offerCompare: domain.offerCompare || { fields: [] },
    jsonStatus: domain.jsonStatus || { dirty: { form: false, rules: false, compare: false } },
    pdfB64: domain.pdfB64 ?? domain.pdf?.base64 ?? domain.pdf?.data ?? null,
    pdfName: domain.pdfName ?? domain.pdf?.fileName ?? domain.pdf?.name ?? null,
    published: domain.published || [],
    extraFormSummary: domain.extraFormSummary || {},
    extraDealSummary: domain.extraDealSummary || {},
    rulesJSON: readRulesJSON(domain),
    initials: domain.initials && typeof domain.initials === "object" && !Array.isArray(domain.initials)
      ? domain.initials
      : null,
    // Verbatim template JSON (object) for raw / non-PDF smart forms, or null.
    rawFormJSON: domain.rawFormJSON ?? null,
  };
};

/**
 * Parses an API response containing an array of StudioFormData objects.
 *
 * @param {Array} jsonArray - Raw API response JSON array
 * @returns {Array} Array of parsed StudioFormData objects
 */
export const parseStudioFormDataList = (jsonArray) => {
  if (!Array.isArray(jsonArray)) return [];
  return jsonArray.map(parseStudioFormData);
};

export default {
  STUDIO_FORM_FIELDS,
  buildStudioFormData,
  parseStudioFormData,
  parseStudioFormDataList,
};
